import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  faFile,
  faImage,
  faLayerGroup,
  faPalette,
  faPlusCircle,
  faTableLayout,
} from "@fortawesome/pro-regular-svg-icons";
import { BuilderToolbarMenuPanel } from "components/builderShell/BuilderToolbarMenuPanel";
import { BuilderToolbarRail } from "components/builderShell/BuilderToolbarRail";
import { useBuilderToolbarDismiss } from "components/builderShell/hooks/useBuilderToolbarDismiss";
import type { IBuilderToolbarRailOption } from "components/builderShell/types";
import {
  IWebsiteBuilderToolbarState,
  THandleSetToolbarMenuType,
  TWebsiteBuilderToolbarMenuType,
} from "../../hooks/toolbarState/useWebsiteBuilderToolbarState";
import { WebsiteBuilderToolbarContext } from "./websiteBuilderToolbarContext";
import { WEBSITE_BUILDER_Z_LAYERS } from "../../utility/websiteBuilderZLayers";
import { WEBSITE_BUILDER_PORTAL } from "../../WebsiteBuilderEditor";
import { WebsiteBuilderSkeleton } from "../../components/skeleton/WebsiteBuilderSkeleton";
import { AddElementsView } from "./menu/views/addElements/AddElementsView";
import { AddMediaView } from "./menu/views/addMedia/AddMediaView";
import { AddSectionsView } from "./menu/views/addSections/AddSectionsView";
import { DesignView } from "./menu/views/design/DesignView";
import { ManagePagesView } from "./menu/views/managePages/ManagePagesView";
import { LayersView } from "./menu/views/layers/LayersView";
import { WEBSITE_BUILDER_BANNER } from "../banner/WebsiteBuilderBanner";

const WEBSITE_BUILDER_MENU_PANEL_WIDTH = 350;

const websiteBuilderToolbarRailStyle = {
  "--builder-toolbar-rail-height": "100%",
  zIndex: WEBSITE_BUILDER_Z_LAYERS.shell.ui,
} as React.CSSProperties;

interface IActiveProps {
  type: "active";
  toolbarState: IWebsiteBuilderToolbarState;
  handleSetToolbarMenuType: THandleSetToolbarMenuType;
}

interface ISkeletonProps {
  type: "skeleton"
}

type TWebsiteBuilderToolbarProps = IActiveProps | ISkeletonProps;

export function WebsiteBuilderToolbar(props: TWebsiteBuilderToolbarProps) {
  const { t } = useTranslation();

  const toolbarPanelRef = useRef<HTMLDivElement>(null);
  const toolbarMenuPanelRef = useRef<HTMLDivElement>(null);

  const toolbarMenuOptions: IBuilderToolbarRailOption<TWebsiteBuilderToolbarMenuType>[] = useMemo(
    () => [
      {
        id: "add-elements",
        tooltip: t("Add elements"),
        icon: faPlusCircle,
      },
      {
        id: "add-sections",
        tooltip: t("Add sections"),
        icon: faTableLayout,
      },
      {
        id: "manage-pages",
        tooltip: t("Manage pages"),
        icon: faFile,
      },
      {
        id: "design",
        tooltip: t("Design"),
        icon: faPalette,
      },
      {
        id: "add-media",
        tooltip: t("Add media"),
        icon: faImage,
      },
      {
        id: "layers",
        tooltip: t("Layers"),
        icon: faLayerGroup,
      },
    ],
    [],
  );

  const possibleMenuType = props.type === "active" ? props.toolbarState.menuType : null;
  const possibleHandleSetToolbarMenuType = props.type === "active" ? props.handleSetToolbarMenuType : null;

  const shouldIgnoreDismissTarget = useCallback((target: Node) => {
    const portal = document.getElementById(WEBSITE_BUILDER_PORTAL);
    const banner = document.getElementById(WEBSITE_BUILDER_BANNER);

    if (!(target instanceof Node)) {
      return true;
    }

    return Boolean(portal?.contains(target) || banner?.contains(target));
  }, []);

  useBuilderToolbarDismiss({
    isOpen: props.type === "active" && possibleMenuType !== "none",
    onClose: () => possibleHandleSetToolbarMenuType?.("none"),
    railRef: toolbarPanelRef,
    panelRef: toolbarMenuPanelRef,
    shouldIgnoreTarget: shouldIgnoreDismissTarget,
    disabled: props.type === "skeleton" || possibleHandleSetToolbarMenuType == null,
  });

  function renderToolbarMenuView() {
    if (props.type === "active") {
      switch (props.toolbarState.menuType) {
        case "add-elements": return <AddElementsView />;
        case "add-sections": return <AddSectionsView />;
        case "manage-pages": return <ManagePagesView />;
        case "design": return <DesignView />;
        case "add-media": return <AddMediaView />;
        case "layers": return <LayersView />;
        case "none":
        default: {
          return null;
        }
      }
    } else {
      return null;
    }
  }

  const menuPanelOpen =
    props.type === "active" &&
    props.toolbarState.menuType !== "none" &&
    !props.toolbarState.closingMenuPanel;

  return (
    <WebsiteBuilderToolbarContext.Provider
      value={{
        toolbarState: props.type === "active" ? props.toolbarState : null,
        handleSetToolbarMenuType: props.type === "active" ? props.handleSetToolbarMenuType : null,
      }}
    >
      {props.type === "skeleton" ? (
        <div
          ref={toolbarPanelRef}
          className="builder-toolbar-rail"
          style={{
            ...websiteBuilderToolbarRailStyle,
            paddingTop: "16px",
            gap: "20px",
          }}
        >
          {toolbarMenuOptions.map((_, index) => (
            <WebsiteBuilderSkeleton
              key={index}
              width={36}
              height={36}
              circle
            />
          ))}
        </div>
      ) : (
        <BuilderToolbarRail
          ref={toolbarPanelRef}
          style={{
            ...websiteBuilderToolbarRailStyle,
            gap: "8px",
          }}
          options={toolbarMenuOptions}
          activeId={props.toolbarState.menuType}
          noneId="none"
          closing={props.toolbarState.closingMenuPanel}
          onSelect={props.handleSetToolbarMenuType}
        />
      )}
      {props.type === "active" && (
        <BuilderToolbarMenuPanel
          ref={toolbarMenuPanelRef}
          open={menuPanelOpen}
          panelWidth={WEBSITE_BUILDER_MENU_PANEL_WIDTH}
          slideTranslateExtra={10}
          className="builder-toolbar-menu-panel--scroll"
          style={{
            zIndex: WEBSITE_BUILDER_Z_LAYERS.shell.underlay,
          }}
        >
          {renderToolbarMenuView()}
        </BuilderToolbarMenuPanel>
      )}
    </WebsiteBuilderToolbarContext.Provider>
  );
}
